'use client';

import type { ReactNode } from 'react';

import { usePathname, useRouter } from 'next/navigation';

/**
 * Matches the two intercepted PDP routes this drawer serves — the creator-scoped
 * `/<url_code>/product/<slug>` and the legacy `/products/<storeId>/<listingId>`.
 */
const PRODUCT_ROUTE = /\/products?\//;

/**
 * Right-anchored slide-in drawer for the intercepted PDP route. Shown when a
 * visitor taps a product on the bio — the storefront stays mounted behind it
 * via the `@modal` parallel slot. Clicking the backdrop (or the in-drawer back
 * button) pops the intercept (`router.back()`), revealing the storefront again.
 *
 * The centered direct-URL page renders `ProductDetail` without this shell.
 */
export function ProductDetailDrawer({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();

  // `cacheComponents` wraps the `@modal` slot in `<Activity>`, which keeps this
  // intercept mounted+frozen even after a soft-nav leaves the product URL — so
  // the slot's `default.tsx` never swaps in and the drawer would linger over the
  // storefront (the back button / backdrop change the URL but leave the panel
  // up). Drive visibility off the LIVE pathname (which DOES update inside the
  // frozen subtree): render nothing once the path is no longer a product route.
  // Mirrors the checkout `ModalShell` dismiss-on-nav fix.
  if (!PRODUCT_ROUTE.test(pathname)) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex justify-end"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => router.back()}
        className="absolute inset-0 cursor-default bg-black/50"
      />
      {/* Width matches the shared `DetailAsideShell` + product
          edit asides — every right-side aside in the dashboard
          reads at 560px. */}
      <aside className="relative flex h-full w-full max-w-[560px] flex-col bg-white shadow-2xl">
        {children}
      </aside>
    </div>
  );
}
